import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SINGLE_USER_GROUP_PERMISSION } from '../../../utils/apollo/permission';
import {
  IUserFieldsFragment,
  IUserGroupFieldsFragment,
  IUserGroupPermissionQuery,
  IUserGroupPermissionQueryVariables,
  UserGroupType,
} from '../../../utils/apollo/graphqlSchema';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../components/PageHeader';
import { Link, useParams } from 'react-router-dom';
import { assertNotNil } from '../../../utils/types';
import { ILinkedTitle } from '../../../components/PageHeader/ClickablePageTitle';
import { appRoutes } from '../../../path';
import PermissionEntityDescribeTable from '../../PermissionControls/PermissionEntityDescribeTable';
import { usePermissionEntityDescribeTable } from '../../PermissionControls/hooks';
import * as PermissionControlsUI from '../../PermissionControls/PermissionControlsUI';
import PermissionEntityToggleButton from '../../PermissionControls/PermissionEntityToggleButton';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setDictionaryListAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum, SortingKindEnum } from '../../../utils/interfaces';
import styled from 'styled-components';
import * as FormUI from '../../pageForm/FormUI';
import FormHeaders from '../../pageForm/FormHeaders';
import GeneralFormItem from '../../../components/GeneralFormItem';
import Avatar from '../../../components/Avatar';
import { ContentLoader } from '../../../components/Loaders';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../../components/ThinDivider';

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const UserNameContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const userHeaders = [
  {
    col: 'Name',
    text: 'Name',
    sortKind: SortingKindEnum.NAME,
    sortable: true,
    order: 1,
    align: 'left',
  },
  {
    col: 'Email',
    text: 'Email',
    sortable: false,
    order: 2,
    align: 'left',
  },
];

const userFormColumnsWidth = '200px minmax(0, 1fr);';

/**
 * UserGroupOverview is a page for viewing a single user group.
 */
const UserGroupOverview = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(
    (state) => state.dictionary.users,
  ) as IUserFieldsFragment[];
  const urlParams = useParams();
  const userGroupId = urlParams.userGroupId;
  assertNotNil(userGroupId);
  const [userGroup, setUserGroup] = useState<IUserGroupFieldsFragment | null>(
    null,
  );
  const { contentList } = usePermissionEntityDescribeTable({
    entity: userGroup,
  });
  const onFetchUserGroup = ({ userGroup }: IUserGroupPermissionQuery) => {
    setUserGroup(userGroup);
    dispatch(
      setDictionaryListAction({
        newValues: userGroup.users,
        dictionaryType: DictionaryTypeEnum.Users,
      }),
    );
  };
  const { loading, refetch } = useQuery<
    IUserGroupPermissionQuery,
    IUserGroupPermissionQueryVariables
  >(GET_SINGLE_USER_GROUP_PERMISSION, {
    variables: {
      id: userGroupId,
    },
    onCompleted: onFetchUserGroup,
  });
  const titleList: ILinkedTitle[] = useMemo(() => {
    const res = [{ name: 'Groups', path: appRoutes.permission.userGroups }];
    if (userGroup) {
      res.push({ name: userGroup.name, path: '' });
    }
    return res;
  }, [userGroup]);

  useEffect(() => {
    return () => {
      dispatch(
        setDictionaryListAction({
          newValues: [],
          dictionaryType: DictionaryTypeEnum.Users,
        }),
      );
    };
  }, [dispatch]);

  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: 'view group',
        id: userGroupId,
      }),
    );
  }, [track, userGroupId]);

  if (!userGroup || loading) {
    return <ContentLoader />;
  }
  return (
    <>
      <ClickablePageTitle titleList={titleList} />
      <PageHeaderDivider />
      <PermissionControlsUI.OverviewContainer>
        <PermissionEntityDescribeTable contentList={contentList} />
        <ThinDivider layout="horizontal" size="full" />
        <Title>Users</Title>
        <FormUI.Container $noMarginTop={true} $noMarginBottom={true}>
          <FormUI.Row $isHeader $gridTemplateColumns={userFormColumnsWidth}>
            <FormHeaders
              dictionaryType={DictionaryTypeEnum.Users}
              headerObject={userHeaders}
            />
          </FormUI.Row>
          {users?.map((user) => {
            return (
              <FormUI.Row
                key={user.id}
                $gridTemplateColumns={userFormColumnsWidth}
              >
                {/* Name and description */}
                <GeneralFormItem
                  label={
                    <Link to={`${appRoutes.permission.users}/${user.id}`}>
                      <UserNameContainer>
                        <Avatar picture={user.picture} size={20} />
                        <span>{user.name}</span>
                      </UserNameContainer>
                    </Link>
                  }
                  tooltipContent={user.name}
                  isBold
                />
                <GeneralFormItem
                  label={user.email}
                  tooltipContent={user.email}
                />
              </FormUI.Row>
            );
          })}
        </FormUI.Container>

        {userGroup.type === UserGroupType.Kubit && (
          <>
            <ThinDivider layout="horizontal" size="full" />
            <PermissionEntityToggleButton
              entity={userGroup}
              refetch={refetch}
              isActive={userGroup.active}
            />
          </>
        )}
      </PermissionControlsUI.OverviewContainer>
    </>
  );
};

export default UserGroupOverview;
